import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  Tooltip,
  useRightInspector,
  useSidebar,
} from "@perlab/ui";
import { useAuth } from "@perlab/auth-bridge";
import { useState } from "react";
import {
  Gauge, PanelLeft, Pin, Settings, Users, X,
} from "lucide-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { clsx } from "clsx";
import { canonicalSubAppId, getSubApp, ZARVIS_APP_ID } from "@perlab/registry";
import { nodeAncestry, useContextChain } from "../state/ContextChain";
import { resolveZarvisIdentity } from "../inspector/zarvisIdentity";
import { buildAppUrl, navTo } from "./ContextChain";
import { ScopeTree } from "./ScopeTree";
import { resolveAppTarget } from "./appTarget";
import { useAppInstances, dedupeDeepestByApp } from "../state/appInstances";
import { useMountScope } from "../state/useMountScope";
import { useChromeVisible } from "../state/useChromeVisible";
import { useScopedFetch } from "../pages/scoped/useScopedFetch";
import { clearAppSessions, deleteAppSession, fetchRecentAppSessions } from "../api/appSessions";
import { type PinItem, usePins } from "../state/usePins";
import { AppIconResolver } from "./AppIconResolver";
import { WorkspaceIcon } from "./WorkspaceIcon";
import { WorkspaceCreateButton } from "./WorkspaceCreateButton";
import { SHELL_BOTTOM_DOCK_CLASS } from "./shellChrome";
import { useEntitlements } from "../../lib/auth/EntitlementsContext";

// ── Nav-restructure (knowledge/07, cat 48): SECTIONS left the sidebar. The per-level facet
//    menu now renders as horizontal tabs under the node title in the content area
//    (NodeFacetHeader + facets/registry — the single owner of the level→facet map). The
//    sidebar is scope tree + mounted apps only. ──

// Spec nav row: 36px tall, square (no radius), full-bleed; 2px FLUSH-left accent (--color-orange-55)
// on active. Theme-semantic surface and text tokens keep the same hierarchy legible in light and
// dark. Inactive carries a transparent 2px border so the row never shifts on selection.
function NavRow({
  icon,
  label,
  active,
  collapsed,
  onClick,
  trailing,
  indent,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  collapsed: boolean;
  onClick: () => void;
  trailing?: React.ReactNode;
  indent?: boolean;
}) {
  return (
    // Row 5.5 (44px hit-area, WCAG 2.5.5): non-interactive row container. The visible bar span keeps its
    // exact grammar (border/bg/hover); the PRIMARY nav is a single absolute-overlay <button> placed
    // INSIDE the bar (so :hover styling still fires), and the row actions (`trailing`) are SIBLINGS of
    // that button — never descendants (WCAG 4.1.2 nested-interactive; AS-A11Y-04 / RD-11).
    // Route/selection (aria-current)/hover/focus/labels/keyboard are unchanged.
    <div className="relative flex min-h-11 w-full items-center">
      <span
        className={clsx(
          "flex h-9 w-full items-center border-l-2 transition-colors",
          collapsed ? "justify-center px-0" : "gap-[11px] px-3",
          indent && !collapsed && "pl-7",
          active
            ? "border-[var(--color-orange-55)] bg-[var(--color-surface-base)] text-[var(--color-text-primary)]"
            : "border-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-text-primary)]",
        )}
      >
        <Tooltip content={label} side="right" disabled={!collapsed}>
          <button
            type="button"
            onClick={onClick}
            aria-current={active ? "page" : undefined}
            aria-label={label}
            className="absolute inset-0"
          />
        </Tooltip>
        <span className="flex shrink-0 items-center justify-center">{icon}</span>
        {!collapsed && <span className="flex-1 truncate text-left text-[13px]">{label}</span>}
        {/* actions sit ABOVE the overlay nav button (relative) and are its SIBLINGS — not nested. */}
        {!collapsed && trailing && <span className="relative">{trailing}</span>}
      </span>
    </div>
  );
}

// Section label per spec: uppercase, 10px, wide tracking, 0 12px padding.
function NavSectionLabel({ children }: { children: React.ReactNode }) {
  // Secondary text resolves to grey-40 on the light sunken surface and grey-60 on the dark frame.
  return (
    <p className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
      {children}
    </p>
  );
}

// Sidebar = ONE scope-adaptive component: fixed workspace header → destinations → mounted apps → footer.
// The MENU never changes between scope states — only the routes/counts and the presence of Mounted-apps.
// Shell features (My Work / Search / Notifications / Ask AI / Help / Profile) live in the TOP BAR.
export function SideNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasRole = () => false } = useAuth();
  const {
    workspaces,
    scopeNodes,
    clients,
    engagements,
    projects,
    workspaceId,
    clientId,
    engagementId,
    projectId,
    appId,
    scopeTreeStatus,
  } = useContextChain();
  const { hasAppAccess } = useEntitlements();
  const isSuperAdmin = hasRole("SuperAdmin");
  const canAccessMissionControl = isSuperAdmin || hasRole("Admin");


  const { mode, displayMode, isMobile } = useSidebar();
  const collapsed = displayMode === "rail" && !isMobile;
  const { pins, removePin } = usePins();
  const { scrollToSection } = useRightInspector();
  const chromeVisible = useChromeVisible();

  const canonicalAppId = appId ? canonicalSubAppId(appId) : undefined;
  const app = canonicalAppId && hasAppAccess(canonicalAppId) ? getSubApp(canonicalAppId) : undefined;
  const workspaceLabel = workspaces.find((workspace) => workspace.id === workspaceId)?.label;
  const zarvisIdentity = resolveZarvisIdentity(workspaceLabel);
  const activeAppLabel = app?.id === ZARVIS_APP_ID ? zarvisIdentity.displayName : app?.name;

  // The MENU never changes between scope states (spec) — only the scope the routes read from + which
  // apps are mounted here.
  const { scopeNodeId } = useMountScope();
  const currentNodeId = scopeNodeId || projectId || engagementId || clientId || workspaceId;

  const openPin = (pin: PinItem): void => {
    if (pin.target_type !== "scope_node") {
      scrollToSection(pin.target_type === "source" ? "source-hub" : "connections");
      return;
    }

    const target = scopeNodes.find((node) => node.id === pin.target_id);
    if (!target) return;
    const targetWorkspaceId = target.node_type === "workspace"
      ? target.id
      : nodeAncestry(target.id, scopeNodes).find((node) => node.node_type === "workspace")?.id;
    if (!targetWorkspaceId) return;
    navigate(
      (target.node_type === "workspace"
        ? navTo.workspaceHome(targetWorkspaceId)
        : navTo.nodeHome(targetWorkspaceId, target.id)) as never,
    );
  };

  // ── OPEN APP region (48.14, amendment #3): the mounted-apps LIST left the rail — with the
  // launcher (readiness), the Apps facet (status) and ⌘K, it was the fifth and weakest app door.
  // The region renders ONLY while an app is active: the open app's row + its screens (the 48.7
  // in-place takeover, unchanged — applies to ANY open app incl. Library; the amendment-#2
  // exclusion was about lists, not the active state). Node browsing = pure scope tree.
  // Door: the active app's deepest visible instance (shared hook — same one-door target as the
  // Apps-tab card + launcher); no placement → launch at the current node.
  const appScope = { ws: workspaceId, client: clientId, eng: engagementId, proj: projectId };
  const instancesState = useAppInstances(currentNodeId);
  const activeInstance = app
    ? dedupeDeepestByApp(instancesState.kind === "ready" ? instancesState.data : []).find((i) => i.app_id === app.id)
    : undefined;

  // ── RECENT APPS (48.17): the actor's working set in this workspace — per-actor app sessions,
  // newest first, display cap 6 (DB-persisted; knowledge/10 R1). One click re-enters the app AT
  // ITS SCOPE (one-door resolveAppTarget); the app itself restores its last screen from its
  // session row (48.18). Refetches on scope/app change so the list tracks the live working set.
  // Library rows excluded (workspace facet, amendment #2). Fail-silent: an error just hides
  // the section — recents are a comfort layer.
  const [recentsBump, setRecentsBump] = useState(0);
  const recentsState = useScopedFetch(
    () => fetchRecentAppSessions(workspaceId),
    [workspaceId, appId, currentNodeId, recentsBump],
  );
  const seenRecentApps = new Set<string>();
  const recents = (recentsState.kind === "ready" ? recentsState.data : [])
    .filter((s) => s.app_id !== "library")
    // 48.21-B: the OPEN context never repeats in recents (it IS the Open App section above);
    .filter((s) => hasAppAccess(canonicalSubAppId(s.app_id)))
    // it reappears the moment you switch away. Kills the duplicate row + its accent.
    .filter((s) => !(canonicalSubAppId(s.app_id) === canonicalAppId && s.scope_node_id === currentNodeId))
    // Collapse canonical and legacy rows for the same app/scope into one recent entry.
    .filter((s) => {
      const key = `${canonicalSubAppId(s.app_id)}@${s.scope_node_id}`;
      if (seenRecentApps.has(key)) return false;
      seenRecentApps.add(key);
      return true;
    })
    .slice(0, 6);
  // Clear recents (own rows; fail-silent — the list just refetches either way).
  const removeRecent = (rAppId: string, rScopeId: string): void => {
    void deleteAppSession(rAppId, rScopeId).catch(() => {}).finally(() => setRecentsBump((b) => b + 1));
  };
  const clearRecents = (): void => {
    void clearAppSessions(workspaceId).catch(() => {}).finally(() => setRecentsBump((b) => b + 1));
  };
  const nameOfNode = (id: string): string =>
    clients.find((c) => c.id === id)?.name
    ?? engagements.find((e) => e.id === id)?.name
    ?? projects.find((p) => p.id === id)?.label
    ?? workspaces.find((w) => w.id === id)?.label
    ?? id;


  // Honor manifest chrome mode — fullscreen / embed / auth all hide the sidebar.
  if (!chromeVisible) return null;

  return (
    <Sidebar>
      {/* Region 1 — FIXED WORKSPACE/TOWER (always present, at every scope). */}
      <SidebarHeader className="!h-12 !px-0">
        {mode === "rail" && !isMobile ? (
          <FixedWorkspaceHeader collapsed={collapsed} />
        ) : (
          <div className="flex h-11 items-center gap-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
            <span>Workspaces</span>
            <WorkspaceCreateButton />
          </div>
        )}
      </SidebarHeader>

      <SidebarBody>
        {/* Region 1 — SCOPE PATH (knowledge/08, cat 48.8–48.10): the EXPLORABLE scope tree —
            workspace groups start collapsed; descendants retain URL-derived expansion.
            Workspace headers toggle, descendant labels navigate. Apps NEVER appear here (A7). */}
        {!collapsed && (
          <>
            {/* 48.15: inside an app the tree collapses to the mount's ancestry spine. */}
            {/* Engagement creation is NOT here (single-door rule, spec 48.24 §157): all creation flows
                through the top-bar "+ Add" door — the retired NewInternalEngagementButton bypassed it. */}
            <ScopeTree includeWorkspaces />
          </>
        )}

        {/* Region 2 — OPEN APP (48.14): renders ONLY while an app is active — its row + screens
            (the 48.7 in-place takeover). Node browsing = pure scope tree; launch/switch lives in
            the top-bar App Launcher / Apps facet / ⌘K. Door = the app's deepest visible instance
            (one-door resolveAppTarget), else launch at the current node. */}
        {app && (
          <>
            {!collapsed && <NavSectionLabel>Open app</NavSectionLabel>}
            <div key={app.id}>
              <NavRow
                icon={app.id === ZARVIS_APP_ID ? (
                  <span className="grid size-5 place-items-center overflow-hidden rounded-full border border-[var(--color-accent)]/60 bg-[var(--color-accent)]/[0.12]">
                    <img
                      src={zarvisIdentity.avatarUrl}
                      alt=""
                      data-testid="zarvis-sidenav-avatar"
                      className="size-full object-cover"
                    />
                  </span>
                ) : <AppIconResolver name={app.icon} />}
                label={activeAppLabel ?? app.name}
                collapsed={collapsed}
                active={false}
                onClick={() =>
                  navigate(
                    (activeInstance
                      ? resolveAppTarget({
                          workspaceId,
                          scopeNodeId: activeInstance.scope_node_id,
                          appId: app.id,
                          readerScopeNodeId: currentNodeId,
                        })
                      : buildAppUrl(appScope.ws, appScope.client, appScope.eng, appScope.proj, app.id)) as never,
                  )
                }
              />
              {/* App SCREENS moved OUT of the sidebar into the app content (AppScreenTabs) — H3 fix,
                  operator-approved deviation from 48.14 — so collapsing the rail no longer strands them. */}
            </div>
          </>
        )}

        {/* Region 3 — RECENT APPS (48.17, below OPEN APP per operator order 2026-07-02): the
            actor's working set (per-actor sessions, newest
            first, cap 6). Click = one-click re-entry at the app's OWN scope; the app restores
            its last screen from its session (48.18). Current app+scope row is highlighted.
            Gated on a successful tree hydration: routing is node-id-direct (resolveAppTarget §6, no path parse),
            but the row's node-NAME (nameOfNode) still resolves against the HYDRATED scope arrays —
            pre-hydration a recents row would render a raw id instead of a name, so we hold the section
            until the tree is loaded (cosmetic gate now; the old mis-routing class is fixed at source). */}
        {!collapsed && scopeTreeStatus === "ready" && recents.length > 0 && (
          <>
            <NavSectionLabel>
              <span className="flex items-center justify-between">
                <span>Recent apps</span>
                <button
                  type="button"
                  title="Clear recent apps"
                  aria-label="Clear recent apps"
                  onClick={clearRecents}
                  className="font-normal normal-case tracking-normal text-[10px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                >
                  Clear
                </button>
              </span>
            </NavSectionLabel>
            {recents.map((s) => {
              const manifest = getSubApp(s.app_id);
              return (
                <NavRow
                  key={s.id}
                  icon={<AppIconResolver name={manifest?.icon ?? ""} />}
                  label={manifest?.name ?? s.app_id}
                  collapsed={false}
                  active={false} // 48.21-B: the current context is filtered out — a recents row is never 'active'
                  trailing={
                    <span className="flex items-center gap-1">
                      <span className="max-w-[84px] truncate text-[10px] text-[var(--color-text-secondary)]">
                        {nameOfNode(s.scope_node_id)}
                      </span>
                      {/* × = remove this entry only (stopPropagation — the row itself navigates);
                          same nested-action pattern as the Pinned rows above. */}
                      <button
                        type="button"
                        title="Remove from recent apps"
                        aria-label={`Remove ${manifest?.name ?? s.app_id} at ${nameOfNode(s.scope_node_id)} from recent apps`}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRecent(s.app_id, s.scope_node_id);
                        }}
                        className="rounded-[2px] p-0.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                      >
                        <X size={12} aria-hidden />
                      </button>
                    </span>
                  }
                  onClick={() => navigate(resolveAppTarget({
                    workspaceId,
                    scopeNodeId: s.scope_node_id,
                    appId: canonicalSubAppId(s.app_id),
                  }) as never)}
                />
              );
            })}
          </>
        )}

        {/* Pinned (preserved) — a pin opens the right inspector, it does not route. */}
        {pins.length > 0 && !collapsed && (
          <>
            <NavSectionLabel>Pinned</NavSectionLabel>
            {pins.map((p) => (
              <NavRow
                key={p.id}
                icon={<Pin size={16} aria-hidden />}
                label={p.label ?? p.target_id}
                collapsed={false}
                onClick={() => openPin(p)}
                trailing={
                  <button
                    type="button"
                    title="Unpin"
                    aria-label={`Unpin ${p.label ?? p.target_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void removePin(p.id);
                    }}
                    className="rounded-[2px] p-0.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                  >
                    <X size={13} aria-hidden />
                  </button>
                }
              />
            ))}
          </>
        )}
        {canAccessMissionControl && (
          <>
            {!collapsed && <NavSectionLabel>Administration</NavSectionLabel>}
            {isSuperAdmin && (
              <NavRow
                icon={<Users size={16} aria-hidden />}
                label="User management"
                collapsed={collapsed}
                active={pathname.endsWith(`/w/${workspaceId}/admin/users`)}
                onClick={() => navigate(navTo.userManagement(workspaceId) as never)}
              />
            )}
            <NavRow
              icon={<Gauge size={16} aria-hidden />}
              label="Mission Control"
              collapsed={collapsed}
              active={pathname.endsWith(`/w/${workspaceId}/admin/mission-control`)}
              onClick={() => navigate(navTo.missionControl(workspaceId) as never)}
            />
          </>
        )}
      </SidebarBody>

      {/* Region 4 — FOOTER (expand/collapse above Settings). */}
      <SidebarFooter className={SHELL_BOTTOM_DOCK_CLASS}>
        <SidebarCollapseItem />
        <SidebarItem
          icon={<Settings size={16} aria-hidden />}
          label="Settings"
          active={pathname.endsWith(`/w/${workspaceId}/settings`)}
          onClick={() => navigate(navTo.settings(workspaceId) as never)}
        />
      </SidebarFooter>
    </Sidebar>
  );
}

function SidebarCollapseItem() {
  const { mode, toggle, isMobile } = useSidebar();
  const label = isMobile
    ? "Close navigation"
    : `${mode === "expanded" ? "Collapse" : "Expand"} (⌘B)`;
  return (
    <SidebarItem
      icon={<PanelLeft size={16} aria-hidden />}
      label={label}
      onClick={toggle}
    />
  );
}

// Workspace switcher — the anchored tenant control at the top of the sidebar (Slack/Linear
// pattern). Owns workspace switching + "New workspace" create (moved off the breadcrumb, which
// no longer carries a workspace level — de-duplicated). Always-visible env tone is the safety cue.
// Bare workspace glyph — no tile fill, consistent with the other sidebar nav glyphs
// (the grey tile read as an odd floating chip). h-8 w-8 box kept only for alignment.
const avatarCls =
  "h-8 w-8 text-[var(--color-accent)] flex items-center justify-center shrink-0";

function FixedWorkspaceHeader({ collapsed }: { collapsed: boolean }): React.ReactElement {
  const { workspaces, workspaceId } = useContextChain();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const workspace = workspaces.find((candidate) => candidate.id === workspaceId)
    ?? { id: workspaceId, label: workspaceId };
  const atHome = pathname.endsWith(`/w/${workspace.id}/home`);

  return (
    <Tooltip content={`${workspace.label} workspace home`} side="right" disabled={!collapsed}>
      <button
        type="button"
        onClick={() => navigate(navTo.workspaceHome(workspace.id))}
        aria-label={`${workspace.label} workspace home`}
        aria-current={atHome ? "page" : undefined}
        title={collapsed ? undefined : `${workspace.label} — workspace home`}
        className={clsx(
          "flex h-11 w-full items-center transition-colors",
          collapsed ? "justify-center" : "gap-2.5 px-2.5 text-left",
          atHome
            ? "bg-[var(--color-surface-base)] shadow-[inset_3px_0_0_var(--color-accent)]"
            : "hover:bg-[var(--color-surface-muted)]",
        )}
      >
        <span aria-hidden className={avatarCls}>
          <WorkspaceIcon workspaceId={workspace.id} label={workspace.label} size={18} />
        </span>
        {!collapsed && (
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text-primary)]">
            {workspace.label}
          </span>
        )}
      </button>
    </Tooltip>
  );
}
